import { andList, formsPhrase } from './pageChecks';

/**
 * Today: approvals that depend on each other ("tinycms UX optimization review
 * v12"). A page that places a form also waiting can't go live first, so the
 * two are one card: "Needs the Tips form (also waiting)", approved together.
 * Several pages sharing forms the agent drafted with them become one card too:
 * "Your agent drafted 3 pages and 2 forms", with Review all.
 */

export interface GroupMember {
  kind: 'page' | 'form';
  id: string;
  title: string | null;
  live: boolean;
  workingHash: string;
}

export interface GroupWords {
  title: string;
  chip: string;
  preview: string;
  note: string;
  cta: string;
  reviewLabel: string;
  done: string;
}

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
const named = (m: GroupMember) => m.title || (m.kind === 'form' ? 'Untitled' : 'Untitled page');

/**
 * What a group's card says, from its pages and forms (in Today's order) and,
 * when its links or home page point at them, the site's look and navigation
 * (`site`: whether that is live yet). A new site from a template is the usual
 * case: header, footer and home page, its pages and their forms all link to
 * each other, so none could be approved alone (backlog bfbfa5d7).
 */
export function groupWords(
  pages: GroupMember[],
  forms: GroupMember[],
  // Credit the agent only when the site has one (a starter's pages aren't its).
  { byAgent = true, site = null }: { byAgent?: boolean; site?: { live: boolean } | null } = {}
): GroupWords {
  const what = forms.length ? formsPhrase(forms.map((f) => ({ name: named(f) }))) : '';
  const pageNames = andList(pages.map(named));
  const total = pages.length + forms.length + (site ? 1 : 0);

  if (site) {
    const withForms = forms.length ? `, with ${what}` : '';
    const pagesWord = count(pages.length, 'page');
    if (!site.live) {
      return {
        title: 'Your site is ready to publish',
        chip: 'New site',
        preview: `Header, footer and ${pagesWord}`,
        note: `The header, footer and ${pagesWord}: ${pageNames}${withForms}. They link to each other, so they go live together.`,
        cta: 'Publish site',
        reviewLabel: 'Review all',
        done: `Your site is live: the header, footer and ${pagesWord}${forms.length ? ` and ${what}` : ''}.`,
      };
    }
    return {
      title: `Menu changes and ${pagesWord} go live together`,
      chip: 'Site change',
      preview: `Menus and ${pagesWord}`,
      note: `The header, footer or home page links to ${pageNames}${withForms}, not live yet. They go live together.`,
      cta: `Approve all ${total}`,
      reviewLabel: 'Review all',
      done: `Published the site’s look and navigation and ${pagesWord}${forms.length ? ` and ${what}` : ''}.`,
    };
  }

  if (!forms.length) {
    // Pages that link to each other, none live yet or some changed.
    const allNew = pages.every((m) => !m.live);
    return {
      title: allNew
        ? byAgent
          ? `Your agent drafted ${count(pages.length, 'page')}`
          : `${count(pages.length, 'page')}, not published yet`
        : `${count(pages.length, 'page')} go live together`,
      chip: allNew ? 'New pages' : 'Page changes',
      preview: count(pages.length, 'page'),
      note: `${pageNames} link to each other. They go live together.`,
      cta: `Approve all ${total}`,
      reviewLabel: 'Review all',
      done: `Published ${pageNames}.`,
    };
  }

  const also =
    forms.length === 1 ? 'also waiting' : forms.length === 2 ? 'both also waiting' : 'all waiting';
  const items = `${count(pages.length, 'page')} and ${count(forms.length, 'form')}`;
  const done = `Published ${pages.length === 1 ? `“${named(pages[0])}”` : count(pages.length, 'page')} and ${what}.`;
  if (pages.length === 1) {
    const page = pages[0];
    return {
      title: named(page),
      chip: page.live ? 'Page change' : 'New page',
      preview: page.title || 'page preview',
      note: `Needs ${what} (${also}). They go live together.`,
      cta: forms.length === 1 ? 'Approve page + form' : `Approve page + ${forms.length} forms`,
      reviewLabel: forms.length === 1 ? 'Review both' : 'Review all',
      done,
    };
  }
  const allNew = [...pages, ...forms].every((m) => !m.live);
  return {
    title: allNew
      ? byAgent
        ? `Your agent drafted ${items}`
        : `${items[0].toUpperCase()}${items.slice(1)}, not published yet`
      : `${items} go live together`,
    chip: allNew ? 'New pages' : 'Page changes',
    preview: items,
    note: `${pageNames}, with ${what} they use. They go live together.`,
    cta: `Approve all ${total}`,
    reviewLabel: 'Review all',
    done,
  };
}
